import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { format, parseISO } from "date-fns";
import {
  BenefitService,
  BENEFIT_CONTACT_TYPE_LABELS,
  BENEFIT_DISCOUNT_TYPE_LABELS,
  BENEFIT_SERVICE_STATUS_LABELS,
} from "../../../entities/admin-benefits";
import {
  renderCaliberShortName,
  getCaliberColor,
} from "../../../shared/lib/caliber";
import { ResidentCaliber } from "../../../entities/admin-events";

interface BenefitsServiceViewDialogProps {
  open: boolean;
  service: BenefitService | null;
  cityNames: Record<number, string>;
  isLoading: boolean;
  isModerating: boolean;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onEditPartner?: () => void;
  onDeletePartner?: () => void;
}

const formatDate = (value?: string) => {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd.MM.yyyy HH:mm");
  } catch {
    return value;
  }
};

const formatDiscount = (service: BenefitService) => {
  const label = BENEFIT_DISCOUNT_TYPE_LABELS[service.discountType];
  if (service.discountType === "percent") {
    return `${label}: ${service.discountValue}%`;
  }
  if (service.discountType === "fixedAmount") {
    return `${label}: ${service.discountValue}`;
  }
  return label;
};

export const BenefitsServiceViewDialog = ({
  open,
  service,
  cityNames,
  isLoading,
  isModerating,
  onClose,
  onApprove,
  onReject,
  onEditPartner,
  onDeletePartner,
}: BenefitsServiceViewDialogProps) => {
  if (!service && !isLoading) return null;

  const canModerate =
    !service?.isPartner && service?.status === "pendingReview";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {service?.isPartner ? "Партнёрская услуга" : "Резидентская услуга"}
      </DialogTitle>
      <DialogContent>
        {isLoading || !service ? (
          <Typography color="text.secondary">Загрузка...</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={
                  service.isPartner ? "Партнёрская" : "Резидентская"
                }
                color={service.isPartner ? "primary" : "default"}
              />
              <Chip
                size="small"
                label={BENEFIT_SERVICE_STATUS_LABELS[service.status]}
              />
              {service.isPromoLive && (
                <Chip size="small" color="success" label="Промо активно" />
              )}
            </Stack>

            {service.photoUrl && (
              <Box
                component="img"
                src={service.photoUrl}
                alt={service.title}
                sx={{
                  width: 128,
                  height: 128,
                  objectFit: "cover",
                  borderRadius: 1,
                }}
              />
            )}

            <Typography variant="h6">{service.title}</Typography>
            <Typography variant="body2" whiteSpace="pre-wrap">
              {service.description}
            </Typography>

            <Divider />

            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemText
                  primary="Категория"
                  secondary={service.categoryName}
                />
              </ListItem>
              {service.isPartner && service.partnerName && (
                <ListItem disableGutters>
                  <ListItemText
                    primary="Партнёр"
                    secondary={service.partnerName}
                  />
                </ListItem>
              )}
              {!service.isPartner && service.owner && (
                <ListItem disableGutters>
                  <ListItemText
                    primary="Владелец"
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          src={service.owner.avatarUrl}
                          sx={{ width: 28, height: 28 }}
                        />
                        <span>
                          {service.owner.firstName} {service.owner.lastName}
                        </span>
                        <Chip
                          size="small"
                          label={renderCaliberShortName(
                            service.owner.caliber as ResidentCaliber,
                          )}
                          sx={{
                            bgcolor: getCaliberColor(
                              service.owner.caliber as ResidentCaliber,
                            ),
                            color: "#fff",
                          }}
                        />
                      </Stack>
                    }
                  />
                </ListItem>
              )}
              <ListItem disableGutters>
                <ListItemText
                  primary="Скидка"
                  secondary={formatDiscount(service)}
                />
              </ListItem>
              {service.promoCode && (
                <ListItem disableGutters>
                  <ListItemText
                    primary="Промокод"
                    secondary={`${service.promoCode} (${formatDate(service.promoStartsAt)} — ${formatDate(service.promoExpiresAt)})`}
                  />
                </ListItem>
              )}
              <ListItem disableGutters>
                <ListItemText
                  primary="Срок действия"
                  secondary={formatDate(service.expiresAt)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Опубликовано"
                  secondary={formatDate(service.publishedAt)}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Создано / обновлено"
                  secondary={`${formatDate(service.createdAt)} / ${formatDate(service.updatedAt)}`}
                />
              </ListItem>
            </List>

            {service.contacts?.length > 0 && (
              <>
                <Typography variant="subtitle2">Контакты</Typography>
                <List dense>
                  {service.contacts.map((contact, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemText
                        primary={BENEFIT_CONTACT_TYPE_LABELS[contact.type]}
                        secondary={contact.value}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {service.cityIds?.length > 0 && (
              <>
                <Typography variant="subtitle2">Города</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {service.cityIds.map((cityId) => (
                    <Chip
                      key={cityId}
                      size="small"
                      label={cityNames[cityId] ?? `ID ${cityId}`}
                    />
                  ))}
                </Stack>
              </>
            )}

            {service.moderationComments &&
              service.moderationComments.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="error">
                    Замечания модератора
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {service.moderationComments.map((comment, index) => (
                      <Typography component="li" variant="body2" key={index}>
                        {comment}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {service?.isPartner && onEditPartner && (
          <Button onClick={onEditPartner}>Редактировать</Button>
        )}
        {service?.isPartner && onDeletePartner && (
          <Button color="error" onClick={onDeletePartner}>
            Удалить
          </Button>
        )}
        {canModerate && onReject && (
          <Button
            color="error"
            startIcon={<ThumbDownIcon />}
            onClick={onReject}
            disabled={isModerating}
          >
            Отклонить
          </Button>
        )}
        {canModerate && onApprove && (
          <Button
            variant="contained"
            color="success"
            startIcon={<ThumbUpIcon />}
            onClick={onApprove}
            disabled={isModerating}
          >
            Одобрить
          </Button>
        )}
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};
